import { Inject, Injectable } from '@angular/core';
import { CurrencyPipe, DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { combineLatest, delay, interval, switchMap, take, takeWhile, tap } from 'rxjs';

import { FuseConfirmationConfig, FuseConfirmationService } from '@fuse/services/confirmation';

import { PlanService } from 'app/modules/partner/services/plan.service';
import { PartnerService } from 'app/modules/partner/services/partner.service';
import { SubscriptionService } from 'app/modules/partner/services/subscription.service';
import { UserService } from 'app/core/user/user.service';

import { SpinnerService } from 'app/modules/shared/services/spinner.service';
import { NotificationService } from 'app/modules/shared/services/notification.service';
import { NavigationService } from 'app/core/navigation/navigation.service';

import { Plan } from 'app/modules/shared/models/plan';
import { Partner } from 'app/modules/shared/models/partner';
import { Subscription } from 'app/modules/shared/models/subscription';
import { User } from 'app/core/user/user.types';

import { PlanUtil } from 'app/modules/shared/utils/plan.util';
import { BackendResponseKeys } from 'app/modules/shared/enums/backendResponseKeys.enum';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';

import {
    PlanSelectionModalComponent,
    PlanSelectionModalData,
    PlanSelectionResult,
} from 'app/modules/shared/components/plan-selection-modal/plan-selection-modal.component';
import {
    CancellationFeedbackDialogComponent,
    CancellationFeedbackResult,
} from 'app/modules/shared/components/cancellation-feedback-dialog/cancellation-feedback-dialog.component';

/**
 * Centralized service for opening the plan upgrade/downgrade modal
 * and handling the subscription change flow.
 *
 * Can be called from any component (navbar, trial banner, etc.) without
 * requiring navigation to the subscription settings page.
 */
@Injectable({
    providedIn: 'root',
})
export class UpgradeService {
    private _currencyPipe = new CurrencyPipe('en-US');

    constructor(
        private _router: Router,
        private _dialog: MatDialog,
        private _planService: PlanService,
        private _subscriptionService: SubscriptionService,
        private _partnerService: PartnerService,
        private _userService: UserService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _spinnerService: SpinnerService,
        private _notificationService: NotificationService,
        private _translocoService: TranslocoService,
        private _navigationService: NavigationService,
        @Inject(DOCUMENT) private _document: Document,
    ) {}

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    /**
     * Handle return from Stripe checkout/upgrade.
     * Reads URL params to detect Stripe return, then starts polling
     * for the subscription change to propagate from Stripe webhooks.
     */
    handleStripeReturn(): void {
        const urlParams = new URLSearchParams(window.location.search);
        const msg = urlParams.get('msg');

        if (msg === BackendResponseKeys.SUBSCRIPTION_UPGRADE_SUCCESS ||
            msg === BackendResponseKeys.SUBSCRIPTION_CHECKOUT_SUCCESS) {
            this._pollForSubscriptionUpdate();
        }
    }

    /**
     * Open the plan selection modal and handle the entire upgrade/downgrade flow.
     * Gathers all required data from existing services, opens the modal,
     * then dispatches to the correct subscription action based on the user's choice.
     */
    openUpgradeModal(): void {
        combineLatest([
            this._planService.availablePlans$,
            this._planService.allPlans$,
            this._subscriptionService.partnerSubscription$,
            this._partnerService.partner$,
            this._userService.user$,
        ])
            .pipe(take(1))
            .subscribe(([availablePlans, allPlans, subscription, partner, user]) => {
                if (!availablePlans?.length || !subscription || !partner || !user) {
                    console.error('UpgradeService: required data not available yet');
                    return;
                }

                const currentPlan = PlanUtil.getPlanById(subscription.planId, allPlans);
                if (!currentPlan) {
                    console.error('UpgradeService: could not resolve current plan');
                    return;
                }

                const isTrialPeriod = subscription.subscriptionStatusRefCode === 'TRL';
                const currencyCode =
                    currentPlan?.currencyRefCode?.toString() ||
                    partner?.currencyRef?.code?.toString() ||
                    'USD';

                // Navigate to subscription page in background so closing the modal lands the user there
                this._router.navigate(['/', PathEnum.APP, PathEnum.APP_PARTNER_SETTINGS, PathEnum.APP_PARTNER_SETTINGS_SUBSCRIPTION]);

                this._openModal(availablePlans, currentPlan, subscription, isTrialPeriod, currencyCode, partner, user);
            });
    }

    // -----------------------------------------------------------------------
    // Private — modal
    // -----------------------------------------------------------------------

    private _openModal(
        availablePlans: Plan[],
        currentPlan: Plan,
        subscription: Subscription,
        isTrialPeriod: boolean,
        currencyCode: string,
        partner: Partner,
        user: User,
    ): void {
        const dialogRef = this._dialog.open(PlanSelectionModalComponent, {
            panelClass: 'fullscreen-dialog',
            enterAnimationDuration: '0ms',
            data: {
                plans: availablePlans,
                currentPlan: currentPlan,
                subscription: subscription,
                isTrialPeriod: isTrialPeriod,
                currencyCode: currencyCode,
            } as PlanSelectionModalData,
        });

        dialogRef.afterClosed().subscribe((result: PlanSelectionResult) => {
            if (!result || result.action === 'cancel') {
                return;
            }

            const sub = new Subscription();
            sub.targetPlanCode = result.selectedPlan.planCode;
            sub.yearlySubscription = result.yearly;
            sub.partner = partner;
            sub.planId = currentPlan.idPlan;

            this._showPlanChangeConfirmation(
                result.action,
                result.selectedPlan,
                result.yearly,
                sub,
                isTrialPeriod,
                currencyCode,
                partner,
                user,
                subscription,
            );
        });
    }

    // -----------------------------------------------------------------------
    // Private — confirmation dialog
    // -----------------------------------------------------------------------

    private _showPlanChangeConfirmation(
        action: string,
        selectedPlan: Plan,
        yearly: boolean,
        sub: Subscription,
        isTrialPeriod: boolean,
        currencyCode: string,
        partner: Partner,
        user: User,
        currentSubscription: Subscription,
    ): void {
        // For downgrade-to-free, show the cancellation feedback dialog instead
        if (action === 'free') {
            this._showCancellationFeedbackDialog(sub, partner, user);
            return;
        }

        const planName = selectedPlan.planName;
        const price = this._formatPriceForConfirmation(selectedPlan, yearly, currencyCode);
        const billingKey = yearly
            ? this._translocoService.translate('settings-partner.subscription.plan-modal.billed-annually')
            : this._translocoService.translate('settings-partner.subscription.plan-modal.billed-monthly');

        let titleKey: string;
        let messageKey: string;
        let confirmKey: string;
        let iconName = 'heroicons_outline:currency-dollar';
        let iconColor: 'primary' | 'warn' | 'accent' | 'basic' | 'info' | 'success' | 'warning' | 'error' = 'primary';
        let confirmColor: 'primary' | 'warn' | 'accent' = 'primary';

        const base = 'settings-partner.subscription.dialogs.confirm-plan-change';

        switch (action) {
            case 'subscribe':
                if (isTrialPeriod) {
                    titleKey = base + '.subscribe-trial-title';
                    messageKey = base + '.subscribe-trial-message';
                    confirmKey = base + '.subscribe-trial-confirm';
                    iconName = 'heroicons_outline:exclamation-triangle';
                    iconColor = 'warn';
                } else {
                    titleKey = base + '.subscribe-title';
                    messageKey = base + '.subscribe-message';
                    confirmKey = base + '.subscribe-confirm';
                }
                break;
            case 'upgrade':
                titleKey = base + '.upgrade-title';
                messageKey = base + '.upgrade-message';
                confirmKey = base + '.upgrade-confirm';
                break;
            case 'downgrade':
                titleKey = base + '.downgrade-title';
                messageKey = base + '.downgrade-message';
                confirmKey = base + '.downgrade-confirm';
                iconName = 'heroicons_outline:exclamation-triangle';
                iconColor = 'warn';
                confirmColor = 'warn';
                break;
            case 'free':
                if (isTrialPeriod) {
                    titleKey = base + '.free-trial-title';
                    messageKey = base + '.free-trial-message';
                    confirmKey = base + '.free-trial-confirm';
                } else {
                    titleKey = base + '.free-title';
                    messageKey = base + '.free-message';
                    confirmKey = base + '.free-confirm';
                }
                iconName = 'heroicons_outline:exclamation-triangle';
                iconColor = 'warn';
                confirmColor = 'warn';
                break;
            default:
                return;
        }

        const config: FuseConfirmationConfig = {
            title: this._translocoService.translate(titleKey, { plan: planName }),
            message: this._translocoService.translate(messageKey, { plan: planName, billing: billingKey, price: price }),
            icon: {
                show: true,
                name: iconName,
                color: iconColor,
            },
            actions: {
                confirm: {
                    show: true,
                    label: this._translocoService.translate(confirmKey),
                    color: confirmColor,
                },
                cancel: {
                    show: true,
                    label: this._translocoService.translate('common.buttons.cancel'),
                },
            },
            dismissible: true,
        };

        const confirmRef = this._fuseConfirmationService.open(config);

        confirmRef.afterClosed().subscribe((confirmResult) => {
            if (confirmResult !== 'confirmed') {
                return;
            }

            switch (action) {
                case 'subscribe':
                    this._callStripeCheckout(sub, user);
                    break;
                case 'upgrade':
                    if (currentSubscription?.subscriptionStripeId) {
                        this._callUpgradeSubscription(sub, user, partner);
                    } else {
                        this._callStripeCheckout(sub, user);
                    }
                    break;
                case 'downgrade':
                    if (PlanUtil.isFreePlan({ planCode: sub.targetPlanCode } as Plan)) {
                        this._callFreeSubscription(sub, user, partner);
                    } else {
                        this._callDowngradeSubscription(sub, user, partner);
                    }
                    break;
                case 'free':
                    this._callFreeSubscription(sub, user, partner);
                    break;
            }
        });
    }

    // -----------------------------------------------------------------------
    // Private — cancellation feedback
    // -----------------------------------------------------------------------

    private _showCancellationFeedbackDialog(sub: Subscription, partner: Partner, user: User): void {
        const dialogRef = this._dialog.open(CancellationFeedbackDialogComponent, {
            width: '500px',
            maxWidth: '95vw',
        });

        dialogRef.afterClosed().subscribe((result: CancellationFeedbackResult | null) => {
            if (!result) return; // User cancelled

            // Save feedback to backend (fire and forget)
            this._subscriptionService
                .saveCancellationFeedback(partner.idPartner, result.reasonCodes)
                .subscribe({ error: (err) => console.error('Failed to save cancellation feedback', err) });

            // Proceed with the downgrade to free
            this._callFreeSubscription(sub, user, partner);
        });
    }

    // -----------------------------------------------------------------------
    // Private — price formatting
    // -----------------------------------------------------------------------

    private _formatPriceForConfirmation(plan: Plan, yearly: boolean, currencyCode: string): string {
        if (PlanUtil.isFreePlan(plan)) {
            return this._translocoService.translate('settings-partner.subscription.plan-modal.free-forever');
        }
        const amount = yearly ? PlanUtil.getMonthlyPricingForYearlyOffer(plan) : plan.planPrice;
        const formatted = this._currencyPipe.transform(amount, currencyCode, 'symbol', '1.0-2') ?? '';
        return formatted + ' / ' + this._translocoService.translate('settings-partner.subscription.plan-modal.monthly').toLowerCase();
    }

    // -----------------------------------------------------------------------
    // Private — API calls
    // -----------------------------------------------------------------------

    private _callStripeCheckout(subscription: Subscription, user: User): void {
        if (!user?.partnerId) {
            this._notificationService.error('User data not available');
            return;
        }

        const currentUrl = new URL(window.location.href);
        const backUrl = currentUrl.pathname + currentUrl.hash;

        this._spinnerService.show();
        this._subscriptionService
            .checkoutSubscription(
                user.partnerId,
                subscription,
                backUrl,
                BackendResponseKeys.SUBSCRIPTION_CHECKOUT_SUCCESS,
                BackendResponseKeys.SUBSCRIPTION_CHECKOUT_CANCELLED,
            )
            .subscribe({
                next: (res) => {
                    this._document.location.href = res.url;
                    window.location.href = res.url;
                },
                error: (err) => {
                    this._spinnerService.hide();
                    console.error('Stripe checkout error:', err);
                },
            });
    }

    private _callUpgradeSubscription(subscription: Subscription, user: User, partner: Partner): void {
        if (!user?.partnerId) {
            this._notificationService.error('User data not available');
            return;
        }

        this._spinnerService.show();
        let responseUpgrade: any;
        this._subscriptionService
            .upgradeSubscription(user.partnerId, subscription)
            .pipe(
                tap((res) => {
                    responseUpgrade = res;
                }),
                delay(1000),
                switchMap(() => this._subscriptionService.getSubscription(partner.idPartner)),
            )
            .subscribe({
                next: (subscriptionUpdated) => {
                    this._subscriptionUpdateAcknowledges(subscriptionUpdated, responseUpgrade.message);
                },
                error: (err) => {
                    this._handlePaymentError(err, partner, user);
                    console.error('Upgrade error:', err);
                },
            });
    }

    private _callDowngradeSubscription(subscription: Subscription, user: User, partner: Partner): void {
        if (!user?.partnerId) {
            this._notificationService.error('User data not available');
            return;
        }

        this._spinnerService.show();
        let responseDowngrade: any;
        this._subscriptionService
            .downgradeSubscription(user.partnerId, subscription)
            .pipe(
                tap((res) => {
                    responseDowngrade = res;
                }),
                delay(1000),
                switchMap(() => this._subscriptionService.getSubscription(partner.idPartner)),
            )
            .subscribe({
                next: (subscriptionUpdated) => {
                    this._subscriptionUpdateAcknowledges(subscriptionUpdated, responseDowngrade.message);
                },
                error: (err) => {
                    this._handlePaymentError(err, partner, user);
                    console.error('Downgrade error:', err);
                },
            });
    }

    private _callFreeSubscription(subscription: Subscription, user: User, partner: Partner): void {
        if (!user?.partnerId) {
            this._notificationService.error('User data not available');
            return;
        }

        this._spinnerService.show();
        let responseFree: any;
        this._subscriptionService
            .subscriptionFreePlan(user.partnerId, subscription)
            .pipe(
                tap((res) => {
                    responseFree = res;
                }),
                delay(1000),
                switchMap(() => this._subscriptionService.getSubscription(partner.idPartner)),
            )
            .subscribe({
                next: (subscriptionUpdated) => {
                    this._subscriptionUpdateAcknowledges(subscriptionUpdated, responseFree.message);
                },
                error: (err) => {
                    this._spinnerService.hide();
                    this._notificationService.error(
                        this._translocoService.translate('settings-partner.subscription.errors.free-plan') ||
                            'An error occurred during the subscription change',
                    );
                    console.error('Free subscription error:', err);
                },
            });
    }

    // -----------------------------------------------------------------------
    // Private — post-action acknowledgement
    // -----------------------------------------------------------------------

    private _subscriptionUpdateAcknowledges(updatedSubscription: Subscription, responseToDisplay: string): void {
        // Update the subscription in the shared service so all subscribers are notified
        this._subscriptionService.partnerSubscription = updatedSubscription;

        // Refresh app-wide state (plans, navigation)
        this._refreshAppState();

        // Hide spinner and show success message
        this._spinnerService.hide();
        if (responseToDisplay) {
            this._notificationService.success(responseToDisplay);
        }
    }

    // -----------------------------------------------------------------------
    // Private — app state refresh
    // -----------------------------------------------------------------------

    /**
     * Refresh app-wide state after a subscription change.
     * Updates available plans and navigation (plan-gated items may have changed).
     */
    private _refreshAppState(): void {
        this._userService.user$.pipe(take(1)).subscribe(user => {
            if (!user?.partnerId) return;

            this._partnerService.partner$.pipe(take(1)).subscribe(partner => {
                if (partner?.currencyRef?.code) {
                    this._planService.getAvailablePlans(user.partnerId, partner.currencyRef.code.toString())
                        .subscribe(plans => {
                            this._planService.availablePlans = plans;
                        });
                }
            });
        });

        // Refresh navigation (plan-gated items may have changed)
        this._navigationService.get().subscribe();
    }

    // -----------------------------------------------------------------------
    // Private — payment error handling
    // -----------------------------------------------------------------------

    /**
     * Handle payment-related errors from upgrade/downgrade operations.
     * Shows a contextual FuseConfirmation dialog with the appropriate message
     * and a recovery action to update the payment method via Stripe billing portal.
     */
    private _handlePaymentError(err: any, partner: Partner, user: User): void {
        this._spinnerService.hide();

        const errorCode = err?.error?.details?.[0];
        const declineCode = err?.error?.details?.[3];

        let messageKey: string;
        let showUpdatePayment = false;

        if (errorCode === '21404') {
            showUpdatePayment = true;
            switch (declineCode) {
                case 'card_declined':
                case 'generic_decline':
                    messageKey = 'settings-partner.subscription.errors.card-declined';
                    break;
                case 'expired_card':
                    messageKey = 'settings-partner.subscription.errors.card-expired';
                    break;
                case 'insufficient_funds':
                    messageKey = 'settings-partner.subscription.errors.insufficient-funds';
                    break;
                default:
                    messageKey = 'settings-partner.subscription.errors.generic-payment-error';
                    break;
            }
        } else if (errorCode === '21403') {
            showUpdatePayment = true;
            messageKey = 'settings-partner.subscription.errors.no-payment-method';
        } else {
            messageKey = 'settings-partner.subscription.errors.plan-change';
        }

        const message = this._translocoService.translate(messageKey);

        if (showUpdatePayment) {
            const config: FuseConfirmationConfig = {
                title: this._translocoService.translate('settings-partner.subscription.errors.payment-error-title'),
                message: message,
                icon: { show: true, name: 'heroicons_outline:credit-card', color: 'warn' },
                actions: {
                    confirm: {
                        show: true,
                        label: this._translocoService.translate('settings-partner.subscription.errors.btn-update-payment'),
                        color: 'primary',
                    },
                    cancel: {
                        show: true,
                        label: this._translocoService.translate('common.buttons.close'),
                    },
                },
                dismissible: true,
            };
            const dialogRef = this._fuseConfirmationService.open(config);
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    this._openBillingPortal(partner, user);
                }
            });
        } else {
            this._notificationService.error(message);
        }
    }

    private _openBillingPortal(partner: Partner, user: User): void {
        const sub = new Subscription();
        sub.partner = partner;
        const currentUrl = new URL(window.location.href);
        const backUrl = currentUrl.pathname + currentUrl.hash;
        this._spinnerService.show();
        this._subscriptionService.manageSubscription(user.partnerId, sub, backUrl).subscribe({
            next: (res) => {
                this._document.location.href = res.url;
                window.location.href = res.url;
            },
            error: () => {
                this._spinnerService.hide();
                this._notificationService.error(
                    this._translocoService.translate('settings-partner.subscription.errors.plan-change'),
                );
            },
        });
    }

    // -----------------------------------------------------------------------
    // Private — Stripe return polling
    // -----------------------------------------------------------------------

    /**
     * Poll for subscription changes after a Stripe checkout/upgrade return.
     * Polls silently every 3s for max 30s (no spinner — toaster already confirms success,
     * and resolvers have already loaded the latest data on page reload).
     * When a change is detected or timeout is reached, refreshes the full app state.
     */
    private _pollForSubscriptionUpdate(): void {
        combineLatest([
            this._subscriptionService.partnerSubscription$,
            this._userService.user$,
        ]).pipe(take(1)).subscribe(([currentSubscription, user]) => {
            if (!currentSubscription || !user?.partnerId) return;

            const previousPlanId = currentSubscription.planId;
            const previousStatus = currentSubscription.subscriptionStatusRefCode;

            const maxAttempts = 10; // 10 x 3s = 30s
            let attempt = 0;
            let changeDetected = false;

            interval(3000).pipe(
                takeWhile(() => attempt < maxAttempts && !changeDetected),
                switchMap(() => {
                    attempt++;
                    return this._subscriptionService.getSubscription(user.partnerId);
                })
            ).subscribe({
                next: (updatedSubscription: Subscription) => {
                    const planChanged = updatedSubscription.planId !== previousPlanId;
                    const statusChanged = updatedSubscription.subscriptionStatusRefCode !== previousStatus;

                    if (planChanged || statusChanged) {
                        changeDetected = true;
                        this._subscriptionService.partnerSubscription = updatedSubscription;
                        this._refreshAppState();
                    }
                },
                complete: () => {
                    if (!changeDetected) {
                        // Timeout reached — do a final refresh attempt
                        this._subscriptionService.getSubscription(user.partnerId).subscribe({
                            next: (sub) => {
                                this._subscriptionService.partnerSubscription = sub;
                                this._refreshAppState();
                            }
                        });
                    }
                }
            });
        });
    }
}
